/**
 * Where each seat can stand, and what it can face from there.
 *
 * Two readings of the same question. `reachableFrom` is the flood fill the
 * boot path has always used: every floor cell connected to a spawn, and every
 * tile next to one. `seatRegions` is the reading a planner needs, and the one
 * the item-flow proof (`itemFlow.ts`) and the oracle share: on a floor two
 * chefs share, a chef can only ever stand where its partner is not always
 * standing between it and — in a one-wide dead end each owns its own end — so
 * the flood fill is narrowed by a joint search over both positions.
 *
 * Pure geometry over `MapContent`; no recipes, no clocks. It imports nothing
 * from `content.ts` at runtime, so `content.ts` can use it without a cycle.
 */

import type { Cell, Direction } from '@overcooked/shared'
import { DIRECTION_VECTORS, cellKey } from '@overcooked/shared'
import type { MapContent } from './content'

/** Directions in a fixed order, so every search breaks ties the same way. */
const DIRECTIONS: readonly Direction[] = ['up', 'down', 'left', 'right']

/** Floor cells a player standing on `from` can reach, and the stations they can face. */
export function reachableFrom(map: MapContent, from: Cell): { floor: Set<string>; faced: Set<string> } {
  const floor = new Set<string>()
  const faced = new Set<string>()
  const queue: Cell[] = [from]

  while (queue.length > 0) {
    const cell = queue.shift() as Cell
    const key = cellKey(cell)
    if (floor.has(key)) continue
    const tile = map.tiles[key]
    if (!tile || tile.type !== 'floor') continue
    floor.add(key)
    for (const v of Object.values(DIRECTION_VECTORS)) {
      const next = { x: cell.x + v.x, y: cell.y + v.y }
      const nextTile = map.tiles[cellKey(next)]
      if (!nextTile) continue
      if (nextTile.type === 'floor') queue.push(next)
      else faced.add(cellKey(next))
    }
  }

  return { floor, faced }
}

/** One seat's floor, and every non-floor tile it can stand in front of. */
export interface SeatRegion {
  /** 1-based, as the spawn digit wrote it. */
  seat: number
  floor: Set<string>
  faced: Set<string>
}

/** Above this many floor cells the joint search is skipped and the flood fill stands. */
export const JOINT_REACH_MAX_FLOOR = 400

/**
 * Every cell each of two chefs on one floor can ever stand on, given that the
 * other is always somewhere too: a breadth-first search over both positions
 * at once, from the spawns, where a joint step moves each chef one cell or
 * leaves it, never onto the cell the other stands on or is stepping into, and
 * never through it. On a floor where they can pass each other the answer is
 * the whole floor for both; in a one-wide dead end each chef keeps its own
 * end. Null when the floor is too big to search (the flood fill stands).
 *
 * `floor` is every floor cell, `index` maps a cell key to its position in it,
 * and `adjacent` lists each floor cell's floor neighbours by index.
 */
export function jointReach(
  floor: Cell[],
  index: Map<string, number>,
  adjacent: number[][],
  a: Cell,
  b: Cell,
): [Set<string>, Set<string>] | null {
  const n = floor.length
  if (n > JOINT_REACH_MAX_FLOOR) return null
  const ia = index.get(cellKey(a))
  const ib = index.get(cellKey(b))
  if (ia === undefined || ib === undefined || ia === ib) return null
  const seen = new Uint8Array(n * n)
  const reachA = new Uint8Array(n)
  const reachB = new Uint8Array(n)
  let frontier = [ia * n + ib]
  seen[ia * n + ib] = 1
  while (frontier.length > 0) {
    const next: number[] = []
    for (const s of frontier) {
      const ca = Math.floor(s / n)
      const cb = s % n
      reachA[ca] = 1
      reachB[cb] = 1
      for (const na of [ca, ...adjacent[ca]]) {
        if (na === cb) continue
        for (const nb of [cb, ...adjacent[cb]]) {
          if (nb === ca || nb === na) continue
          const key = na * n + nb
          if (seen[key]) continue
          seen[key] = 1
          next.push(key)
        }
      }
    }
    frontier = next
  }
  const cells = (reach: Uint8Array): Set<string> => {
    const out = new Set<string>()
    reach.forEach((hit, i) => {
      if (hit) out.add(cellKey(floor[i]))
    })
    return out
  }
  return [cells(reachA), cells(reachB)]
}

/** Every non-floor tile a chef standing on `cells` can face, walls included (as `reachableFrom` reports). */
export function facedFrom(map: MapContent, cells: ReadonlySet<string>): Set<string> {
  const faced = new Set<string>()
  for (const key of cells) {
    const [x, y] = key.split(',').map(Number)
    for (const d of DIRECTIONS) {
      const v = DIRECTION_VECTORS[d]
      const next = cellKey({ x: x + v.x, y: y + v.y })
      const tile = map.tiles[next]
      if (tile && tile.type !== 'floor') faced.add(next)
    }
  }
  return faced
}

/**
 * Each seat's region: the flood fill from its spawn, narrowed by `jointReach`
 * when exactly two seats share one floor and cannot always pass each other.
 *
 * On a split kitchen, a solo one, and a shared floor with room to pass, this
 * is `reachableFrom` exactly.
 */
export function seatRegions(map: MapContent): SeatRegion[] {
  const regions: SeatRegion[] = map.spawns.map((spawn, i) => {
    const reach = reachableFrom(map, spawn)
    return { seat: i + 1, floor: reach.floor, faced: reach.faced }
  })
  if (regions.length !== 2) return regions
  const shared = [...regions[0].floor].some((key) => regions[1].floor.has(key))
  if (!shared) return regions

  const floor = Object.values(map.tiles)
    .filter((t) => t.type === 'floor')
    .map((t) => ({ ...t.cell }))
    .sort((a, b) => a.y - b.y || a.x - b.x)
  const index = new Map<string, number>()
  floor.forEach((cell, i) => index.set(cellKey(cell), i))
  const adjacent = floor.map((cell) => {
    const out: number[] = []
    for (const d of DIRECTIONS) {
      const v = DIRECTION_VECTORS[d]
      const j = index.get(cellKey({ x: cell.x + v.x, y: cell.y + v.y }))
      if (j !== undefined) out.push(j)
    }
    return out
  })
  const reachable = jointReach(floor, index, adjacent, map.spawns[0], map.spawns[1])
  if (!reachable) return regions
  return regions.map((region, i) =>
    reachable[i].size === region.floor.size
      ? region
      : { seat: region.seat, floor: reachable[i], faced: facedFrom(map, reachable[i]) },
  )
}
